import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { CalendarClock, Clock, History, ReceiptText, ShieldAlert, WalletCards } from 'lucide-react'
import {
  NavigationLink,
  Banner,
  Card,
  Chip,
  DetailHero,
  Drawer,
  DrawerSection,
  EmptyState,
  ErrorState,
  FilterToolbar,
  LoadMore,
  KeyValueGrid,
  MoneyValue,
  MoneyMovementSummary,
  MoneyReconciliation,
  MoneyListSummary,
  Skeleton,
  StatusBadge,
  TechnicalDetails,
} from '@shared/design-system/ui'
import { useMediaQuery } from '@shared/lib/use-media-query'
import { formatMoney as formatCurrencyMoney } from '@shared/lib/format/currency'
import {
  balanceName,
  bucketKey,
  experienceLabel,
  hasOpenDebt,
  ledgerKindLabel,
  nextReleaseDate,
  withdrawalExperience,
} from '@entities/wallet'
import type { BalanceSummary } from '@entities/wallet'
import {
  alignRight,
  availableBlock,
  balanceGrid,
  buyerCell,
  buyerName,
  buyerProduct,
  bucketCard,
  bucketExperience,
  bucketFooter,
  bucketHeader,
  bucketName,
  bucketTitleBox,
  clickableRow,
  debtMetric,
  debtMetricValue,
  fieldLabel,
  header,
  helpText,
  iconTile,
  hideOnMobile,
  kpiValue,
  ledgerDetail,
  ledgerDetailLabel,
  ledgerDetailRow,
  ledgerDetailRows,
  ledgerDetailValue,
  ledgerReference,
  ledgerReferenceLead,
  ledgerReferenceText,
  ledgerReferenceTitle,
  metric,
  metricCard,
  metricCount,
  metricHeader,
  metricLabel,
  panel,
  panelSubtitle,
  panelTitle,
  penaltyCaption,
  penaltyCount,
  penaltyFigure,
  metricValue,
  mobileCard,
  mobileCardHeader,
  mobileCardLead,
  mobileList,
  mobileMetaGrid,
  mobileMetaItem,
  mobileMetaLabel,
  mobileMetaValue,
  muted,
  page,
  receiptLine,
  resultCount,
  secondaryMetrics,
  statGrid,
  statusOption,
  subtitle,
  table,
  tableCard,
  tableViewport,
  td,
  th,
  title,
  titleBlock,
  value,
  viewTabs,
} from './admin.css'
import {
  useAdminBalance,
  useAdminChargebackPenalties,
  useAdminReceivablesInfinite,
  useAdminReceivablesSummary,
  useAdminReceivableCurrencies,
  useAdminStatementInfinite,
  useAdminUsersLookup,
  exportAdminReceivables,
  exportAdminStatement,
} from '../api/admin.api'
import type { AdminLookupUser, ExportFormat, LedgerItem, MoneyDto } from '../api/admin.api'
import { ExportButton } from './ExportButton'
import type { AdminStatementItem, AdminStatementView, StatementJournalItem } from '../api/admin.api'
import { downloadBlob, exportTimestamp } from '../lib/download-xlsx'
import { balanceHasMoney, sortCurrencies, sumLedgerByCurrency } from '../lib/admin-money'
import type { MoneySummary } from '../lib/admin-money'
import { compactTechnicalId } from './technical-id'
import { adminUserDisplay } from './admin-user-display'
import { AdminSaleDetailDrawer } from './AdminSaleDetailDrawer'
import { AdminDashboardFilters } from './AdminDashboardFilters'
import type { DashboardSelection } from './AdminDashboardFilters'
import { AdminFinanceHourlyChart } from './AdminFinanceHourlyChart'
import { adminBalanceAsSummary } from '../lib/admin-balance'
import {
  DEFAULT_DASHBOARD_PERIOD,
  adminListPeriodRange,
  dashboardPeriodLabel,
  dashboardRangeLabel,
} from '../lib/admin-dashboard-period'

type FinanceView = 'receivables' | 'statement'

const financeViewOptions: ReadonlyArray<{ id: FinanceView; label: string }> = [
  { id: 'receivables', label: 'Recebíveis' },
  { id: 'statement', label: 'Extrato' },
]

const statementViewOptions: ReadonlyArray<{ id: AdminStatementView; label: string }> = [
  { id: 'journal', label: 'Movimentações' },
  { id: 'ledger', label: 'Visão técnica' },
]

function tileIcon(): ReactNode {
  return <History size={19} />
}

function experienceIcon(): ReactNode {
  return <Clock size={12} />
}

function formatDateTime(valueToFormat: string) {
  return new Date(valueToFormat).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDateOnly(valueToFormat: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(valueToFormat))
}

function formatShortDate(valueToFormat: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(valueToFormat))
}

function formatMoney(valueToFormat: MoneyDto): string {
  return formatCurrencyMoney(valueToFormat.amount, valueToFormat.currency)
}

function MetricMoneyList({
  rows,
  emptyCurrency = 'BRL',
}: {
  rows: MoneySummary[]
  emptyCurrency?: string
}) {
  if (rows.length === 0) {
    return (
      <span className={kpiValue}>
        <MoneyValue amount="0.000" currency={emptyCurrency} size="lg" />
      </span>
    )
  }
  return (
    <span className={kpiValue}>
      {rows.map((row) => (
        <MoneyValue amount={row.amount} currency={row.currency} key={row.currency} size="lg" />
      ))}
    </span>
  )
}

function enumLabel(valueToFormat: string) {
  return valueToFormat
    .toLowerCase()
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function ledgerEntryTitle(item: LedgerItem) {
  if (item.type === 'USER') return 'Saldo do usuário'
  if (item.type === 'RESERVE') return 'Reserva'
  if (item.type === 'VENDEPAY') return 'Receita Vendepay'
  if (item.type === 'DEBT') return 'Dívida'
  return enumLabel(item.type)
}

function isStatementJournalItem(item: AdminStatementItem): item is StatementJournalItem {
  return 'eventType' in item
}

function statementEventTypeLabel(type: StatementJournalItem['eventType']) {
  const labels: Record<StatementJournalItem['eventType'], string> = {
    SALE_SETTLED: 'Venda',
    SALE_REVOKED: 'Venda revertida',
    DEBT_CREATED: 'Débito',
    DEBT_SETTLED: 'Débito',
    DEBT_REVERSED: 'Dívida cancelada',
    WITHDRAWAL_REQUESTED: 'Recebimento pendente',
    WITHDRAWAL_APPROVED: 'Recebimento em processamento',
    WITHDRAWAL_REFUSED: 'Recebimento cancelado',
    WITHDRAWAL_COMPLETED: 'Recebimento concluído',
    WITHDRAWAL_REVERTED: 'Recebimento revertido',
    WITHDRAWAL_OBSERVED: 'Recebimento observado pela Stripe',
    CONVERSION: 'Conversão',
  }
  return labels[type]
}

function isDebtMovement(item: AdminStatementItem): boolean {
  return (
    isStatementJournalItem(item) &&
    ['DEBT_CREATED', 'DEBT_SETTLED', 'DEBT_REVERSED', 'SALE_REVOKED'].includes(item.eventType)
  )
}

function statementTitle(item: StatementJournalItem) {
  if (item.eventType === 'SALE_SETTLED') return 'Crédito da venda'
  const title = item.detail.title
  return typeof title === 'string' && title.length > 0
    ? title
    : statementEventTypeLabel(item.eventType)
}

function statementDetailMoney(item: StatementJournalItem, key: string): MoneyDto | undefined {
  const valueToRead = item.detail[key]
  if (
    typeof valueToRead === 'object' &&
    valueToRead !== null &&
    typeof (valueToRead as { amount?: unknown }).amount === 'string' &&
    typeof (valueToRead as { currency?: unknown }).currency === 'string'
  ) {
    return valueToRead as MoneyDto
  }
  return undefined
}

function FxLossDetails({ item }: { item: StatementJournalItem }) {
  const fx = item.detail.fx
  if (!fx || typeof fx !== 'object' || Array.isArray(fx)) return null
  const data = { ...item, detail: fx as Record<string, unknown> }
  const original = statementDetailMoney(data, 'originalBasis')
  const debit = statementDetailMoney(data, 'providerDebit')
  const loss = statementDetailMoney(data, 'loss')
  if (!original || !debit || !loss) return null
  return (
    <DrawerSection
      title="Perda cambial"
      description="A parcela deste lançamento segue a participação da pessoa na venda."
    >
      <KeyValueGrid
        items={[
          { label: 'Base original em reais', value: <MoneyValue {...original} size="sm" /> },
          { label: 'Débito na Stripe', value: <MoneyValue {...debit} size="sm" /> },
          { label: 'Perda total da cobrança', value: <MoneyValue {...loss} size="sm" /> },
          { label: 'Parcela deste lançamento', value: <MoneyValue {...item.amount} size="sm" /> },
        ]}
      />
      {typeof data.detail.referenceId === 'string' ? (
        <TechnicalDetails
          rows={[{ label: 'Referência na Stripe', value: data.detail.referenceId }]}
        />
      ) : null}
    </DrawerSection>
  )
}

function statementDetailString(item: StatementJournalItem, key: string): string | undefined {
  const valueToRead = item.detail[key]
  return typeof valueToRead === 'string' && valueToRead.length > 0 ? valueToRead : undefined
}

function statementDetailMeta(item: StatementJournalItem): string | null {
  const parts: string[] = []
  const userAmount = statementDetailMoney(item, 'userAmount')
  const reserveAmount = statementDetailMoney(item, 'reserveAmount')
  const remainingAmount = statementDetailMoney(item, 'remainingAmount')
  const sourceAmount = statementDetailMoney(item, 'sourceAmount')
  const availableAt = statementDetailString(item, 'availableAt')

  if (userAmount) {
    parts.push(
      `${item.direction === 'IN' ? '+' : ''}${formatMoney(userAmount)}, ${statementAvailabilityLabel(
        item,
        availableAt ? formatShortDate(availableAt) : undefined,
      )}`,
    )
  }
  if (reserveAmount) {
    parts.push(`${formatMoney(reserveAmount)} retido`)
  }
  if (remainingAmount) {
    parts.push(`pendente nesta data: ${formatMoney(remainingAmount)}`)
  }
  if (sourceAmount && !userAmount) {
    parts.push(`${formatMoney(sourceAmount)} solicitado`)
  }

  return parts.length > 0 ? parts.join(' · ') : null
}

function statementSubtitle(item: StatementJournalItem) {
  return [
    item.saleId ? `Venda ${compactTechnicalId(item.saleId)}` : null,
    isDebtMovement(item) ? null : statementDetailMeta(item),
    `moeda ${item.currency}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

function statementEventTone(type: StatementJournalItem['eventType']) {
  if (type === 'SALE_SETTLED' || type === 'DEBT_REVERSED') return 'success'
  if (type === 'WITHDRAWAL_REFUSED' || type === 'WITHDRAWAL_REVERTED') return 'closed'
  if (type === 'WITHDRAWAL_REQUESTED' || type === 'WITHDRAWAL_COMPLETED') return 'awaiting'
  return 'informative'
}

function financeEntryTitle(item: AdminStatementItem) {
  return isStatementJournalItem(item) ? statementTitle(item) : ledgerEntryTitle(item)
}

function financeEntrySubtitle(item: AdminStatementItem) {
  return isStatementJournalItem(item) ? statementSubtitle(item) : ledgerEntrySubtitle(item)
}

function financeEntrySaleId(item: AdminStatementItem) {
  return isStatementJournalItem(item) ? item.saleId : item.saleId
}

function financeEntryDate(item: AdminStatementItem) {
  return isStatementJournalItem(item) ? item.occurredAt : item.createdAt
}

function financeEntryStatusLabel(item: AdminStatementItem) {
  return isStatementJournalItem(item)
    ? statementEventTypeLabel(item.eventType)
    : ledgerStatusLabel(item.status)
}

function financeEntryStatusTone(item: AdminStatementItem) {
  return isStatementJournalItem(item)
    ? statementEventTone(item.eventType)
    : ledgerStatusTone(item.status)
}

function ledgerEntrySubtitle(item: LedgerItem) {
  return [
    adminLedgerSourceLabel(item.source),
    item.role ? ledgerRoleLabel(item.role) : null,
    `moeda ${item.amount.currency}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

function ledgerRoleLabel(role: string) {
  return role === 'rounding-bonus' ? 'Crédito ao vendedor' : enumLabel(role)
}

function adminLedgerSourceLabel(source: string) {
  const labels: Record<string, string> = {
    SALE: 'Venda',
    WITHDRAWAL_REMAINDER: 'Ajuste de recebimento',
    DEBT_REMAINDER: 'Sobra de reserva após reembolso',
    CURRENCY_CONVERSION: 'Conversão',
  }
  return labels[source] ?? enumLabel(source)
}

function ledgerStatusLabel(status: string) {
  const labels: Record<string, string> = {
    PENDING: 'A liberar',
    PROCESSING: 'Processando',
    PROCESSING_PARTIAL: 'Parcial',
    PAID: 'Pago',
    REVOKED: 'Revogado',
    FAILED: 'Falhou',
    DEBT_SETTLED: 'Débito',
    DEBT_SETTLED_PARTIAL: 'Dívida abatida parcial',
  }

  return labels[status] ?? enumLabel(status)
}

function isPendingLedger(item: LedgerItem) {
  return (
    item.status === 'PENDING' ||
    item.status === 'PROCESSING' ||
    item.status === 'PROCESSING_PARTIAL'
  )
}

function AdminBalanceCards({ balances }: { balances: BalanceSummary[] }) {
  return (
    <div className={balanceGrid}>
      {balances.map((balance) => {
        const experience = withdrawalExperience()
        const release = nextReleaseDate(balance)
        const debtOpen = hasOpenDebt(balance)
        return (
          <Card key={bucketKey(balance)} padding="md" className={bucketCard({ experience })}>
            <div className={bucketHeader}>
              <span className={iconTile({ experience })}>{tileIcon()}</span>
              <div className={bucketTitleBox}>
                <span className={bucketName}>{balanceName(balance)}</span>
                <span className={bucketExperience({ experience })}>
                  {experienceIcon()}
                  {experienceLabel()}
                </span>
              </div>
            </div>

            <div className={availableBlock}>
              <span className={fieldLabel}>Disponível</span>
              <MoneyValue
                amount={balance.available.amount}
                currency={balance.available.currency}
                size="xl"
              />
            </div>

            <div className={secondaryMetrics}>
              <div className={metric}>
                <span className={metricLabel}>A receber</span>
                <span className={metricValue}>
                  <MoneyValue
                    amount={balance.pending.amount}
                    currency={balance.pending.currency}
                    size="sm"
                  />
                </span>
              </div>
              <div className={metric}>
                <span className={metricLabel}>Reservado</span>
                <span className={metricValue}>
                  <MoneyValue
                    amount={balance.reserve.amount}
                    currency={balance.reserve.currency}
                    size="sm"
                  />
                </span>
              </div>
              <div className={metric}>
                <span className={metricLabel}>Em recebimento</span>
                <span className={metricValue}>
                  <MoneyValue
                    amount={balance.processingWithdrawal.amount}
                    currency={balance.processingWithdrawal.currency}
                    size="sm"
                  />
                </span>
              </div>
              <div className={metric}>
                <span className={metricLabel}>Em recuperação de dívida</span>
                <span className={metricValue}>
                  <MoneyValue {...balance.processingDebtRecovery} size="sm" />
                </span>
              </div>
            </div>

            {debtOpen ? (
              <div className={`${metric} ${debtMetric}`}>
                <span className={metricLabel}>
                  <ShieldAlert size={12} /> Dívida em aberto
                </span>
                <span className={debtMetricValue}>
                  <MoneyValue
                    amount={balance.openDebt.amount}
                    currency={balance.openDebt.currency}
                    size="sm"
                  />
                </span>
              </div>
            ) : null}

            <div className={bucketFooter}>
              <div className={receiptLine}>
                <CalendarClock size={16} />
                {release
                  ? `Próxima liberação: ${release}`
                  : balance.awaitingAvailability && Number(balance.awaitingAvailability.amount) > 0
                    ? `Aguardando previsão de liberação em ${balance.currency}`
                    : `Sem liberação prevista em ${balance.currency}`}
              </div>
              <span className={helpText}>
                Visão administrativa somente leitura do saldo e da rota financeira do usuário.
              </span>
            </div>
          </Card>
        )
      })}
    </div>
  )
}

function ledgerStatusTone(status: string) {
  if (status === 'PAID' || status.includes('SETTLED')) return 'success'
  if (status === 'PENDING' || status === 'PROCESSING' || status === 'PROCESSING_PARTIAL') {
    return 'awaiting'
  }
  if (status === 'REVOKED' || status === 'FAILED') return 'closed'
  return 'neutral'
}

function StatementMoney({ item, size = 'sm' }: { item: AdminStatementItem; size?: 'sm' | 'xl' }) {
  const sign = isStatementJournalItem(item)
    ? { IN: '+ ', OUT: '− ', INFO: '', HELD: '' }[item.direction]
    : ''
  return (
    <span>
      {sign}
      <MoneyValue amount={item.amount.amount} currency={item.amount.currency} size={size} />
    </span>
  )
}

function AdminStatementList({
  items,
  usersById,
  usersLoading = false,
  filtered = false,
  isMobile = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  onSelect,
}: {
  items: AdminStatementItem[]
  usersById: ReturnType<typeof useAdminUsersLookup>['usersById']
  usersLoading?: boolean
  filtered?: boolean
  isMobile?: boolean
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
  onSelect: (item: AdminStatementItem) => void
}) {
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<ReceiptText size={22} />}
        title={filtered ? 'Nada neste filtro' : 'Sem lançamentos'}
        description={
          filtered
            ? 'Ajuste o período ou selecione outro usuário para ver outros lançamentos.'
            : 'As vendas e ajustes aparecem aqui quando houver movimentação.'
        }
      />
    )
  }

  return (
    <>
      {isMobile ? (
        <div className={mobileList}>
          {items.map((item) => {
            const userDisplay = item.recipientId
              ? adminUserDisplay({
                  userId: item.recipientId,
                  usersById,
                  isLoading: usersLoading,
                })
              : null
            const saleId = financeEntrySaleId(item)

            return (
              <div
                key={item.id}
                className={mobileCard}
                role="button"
                tabIndex={0}
                aria-label={`Ver lançamento ${financeEntryTitle(item)}`}
                onClick={() => onSelect(item)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelect(item)
                  }
                }}
              >
                <div className={mobileCardHeader}>
                  <span className={mobileCardLead}>
                    <span className={buyerName}>{financeEntryTitle(item)}</span>
                    <span className={buyerProduct}>{financeEntrySubtitle(item)}</span>
                  </span>
                  <StatusBadge
                    label={financeEntryStatusLabel(item)}
                    tone={financeEntryStatusTone(item)}
                    size="sm"
                  />
                </div>
                <div className={mobileMetaGrid}>
                  <span className={mobileMetaItem}>
                    <span className={mobileMetaLabel}>Valor</span>
                    <span className={mobileMetaValue}>
                      <StatementMoney item={item} size="sm" />
                    </span>
                  </span>
                  <span className={mobileMetaItem}>
                    <span className={mobileMetaLabel}>Venda</span>
                    <span className={mobileMetaValue}>
                      {saleId ? `Venda ${compactTechnicalId(saleId)}` : 'Sem venda'}
                    </span>
                    <span className={buyerProduct}>{formatDateTime(financeEntryDate(item))}</span>
                  </span>
                  <span className={mobileMetaItem}>
                    <span className={mobileMetaLabel}>Usuário</span>
                    <span className={mobileMetaValue}>{userDisplay?.primary ?? 'Vendepay'}</span>
                    {userDisplay?.secondary ? (
                      <span className={buyerProduct}>{userDisplay.secondary}</span>
                    ) : null}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className={tableViewport}>
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>Lançamento</th>
                <th className={`${th} ${hideOnMobile}`}>Venda</th>
                <th className={th}>Usuário</th>
                <th className={th}>Status</th>
                <th className={`${th} ${alignRight}`}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const userDisplay = item.recipientId
                  ? adminUserDisplay({
                      userId: item.recipientId,
                      usersById,
                      isLoading: usersLoading,
                    })
                  : null
                const saleId = financeEntrySaleId(item)

                return (
                  <tr
                    key={item.id}
                    className={clickableRow}
                    role="button"
                    tabIndex={0}
                    aria-label={`Ver lançamento ${financeEntryTitle(item)}`}
                    onClick={() => onSelect(item)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onSelect(item)
                      }
                    }}
                  >
                    <td className={td}>
                      <span className={buyerCell}>
                        <span className={buyerName}>{financeEntryTitle(item)}</span>
                        <span className={buyerProduct}>{financeEntrySubtitle(item)}</span>
                      </span>
                    </td>
                    <td className={`${td} ${hideOnMobile}`}>
                      <span className={buyerCell}>
                        <span className={value}>
                          {saleId ? `Venda ${compactTechnicalId(saleId)}` : 'Sem venda'}
                        </span>
                        <span className={buyerProduct}>
                          {formatDateTime(financeEntryDate(item))}
                        </span>
                      </span>
                    </td>
                    <td className={td}>
                      <span className={buyerCell}>
                        <span className={buyerName}>{userDisplay?.primary ?? 'Vendepay'}</span>
                        {userDisplay?.secondary ? (
                          <span className={buyerProduct}>{userDisplay.secondary}</span>
                        ) : null}
                      </span>
                    </td>
                    <td className={td}>
                      <StatusBadge
                        label={financeEntryStatusLabel(item)}
                        tone={financeEntryStatusTone(item)}
                        size="sm"
                      />
                    </td>
                    <td className={`${td} ${alignRight}`}>
                      <StatementMoney item={item} size="sm" />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <MoneyMovementSummary
        movements={items.filter(isStatementJournalItem).flatMap((item) => {
          const reserve = statementDetailMoney(item, 'reserveAmount')
          return item.eventType === 'SALE_SETTLED' && item.direction === 'IN' && reserve
            ? [item, { direction: 'HELD', amount: reserve }]
            : [item]
        })}
      />
      <MoneyListSummary
        label="Lançamentos carregados"
        values={items.filter((item) => !isStatementJournalItem(item)).map((item) => item.amount)}
      />
      {onLoadMore ? (
        <LoadMore
          onClick={onLoadMore}
          loading={loadingMore}
          shown={items.length}
          hasMore={hasMore}
          label="Carregar mais"
        />
      ) : null}
    </>
  )
}

function moneyFromUnknown(valueToRead: unknown): MoneyDto | null {
  if (
    typeof valueToRead === 'object' &&
    valueToRead !== null &&
    typeof (valueToRead as { amount?: unknown }).amount === 'string' &&
    typeof (valueToRead as { currency?: unknown }).currency === 'string'
  ) {
    return valueToRead as MoneyDto
  }
  return null
}

function statementDetailRecords(valueToRead: unknown): Record<string, unknown>[] {
  return Array.isArray(valueToRead)
    ? valueToRead.filter(
        (item): item is Record<string, unknown> => typeof item === 'object' && item !== null,
      )
    : []
}

function statementDetailKey(valueToRead: unknown, fallback: number): string {
  return typeof valueToRead === 'string' || typeof valueToRead === 'number'
    ? String(valueToRead)
    : String(fallback)
}

function StatementDetailMoney({ valueToRead }: { valueToRead: unknown }) {
  const money = moneyFromUnknown(valueToRead)
  return money ? <MoneyValue amount={money.amount} currency={money.currency} size="sm" /> : null
}

function StatementDetailPreciseMoney({ valueToRead }: { valueToRead: unknown }) {
  const money = moneyFromUnknown(valueToRead)
  if (!money) {
    return null
  }
  return <MoneyValue amount={money.amount} currency={money.currency} fractionDigits={3} size="sm" />
}

function StatementDetailSummaryRows({ item }: { item: StatementJournalItem }) {
  const observed = item.eventType === 'WITHDRAWAL_OBSERVED'
  const stripeLabels = new Map<string, string>([
    ['pending', 'Pendente'],
    ['in_transit', 'Em transferência bancária'],
    ['paid', 'Pago pela Stripe'],
    ['failed', 'Falhou'],
    ['canceled', 'Cancelado'],
  ])
  const ledgerLabels = new Map<string, string>([
    ['pending', 'Conciliação financeira pendente'],
    ['completed', 'Efeito financeiro aplicado'],
    ['reversed', 'Efeito financeiro revertido'],
  ])
  const rows = [
    {
      label: 'Valor do participante',
      value: statementDetailMoney(item, 'userAmount'),
      precise: false,
    },
    {
      label: 'Retido em garantia',
      value: statementDetailMoney(item, 'reserveAmount'),
      precise: false,
    },
    {
      label: 'Pendente nesta data',
      value: statementDetailMoney(item, 'remainingAmount'),
      precise: false,
    },
    {
      label: 'Devolvido ao saldo',
      value: statementDetailMoney(item, 'restoredAmount'),
      precise: false,
    },
    {
      label: 'Dívida pendente cancelada (sem entrada de dinheiro)',
      value: statementDetailMoney(item, 'outstandingAmount'),
      precise: false,
    },
    {
      label: 'Valor solicitado',
      value: statementDetailMoney(item, 'sourceAmount'),
      precise: false,
    },
  ].filter((row) => row.value !== undefined)
  const availableAt = statementDetailString(item, 'availableAt')

  return (
    <>
      {observed ? (
        <Banner tone="info" title="Observação da Stripe">
          Este evento registra o recebimento observado e não movimenta o saldo. Não representa
          confirmação bancária.
        </Banner>
      ) : null}
      {observed ? (
        <KeyValueGrid
          items={[
            {
              label: 'Estado informado pela Stripe',
              value: stripeLabels.get(String(item.detail.status)) ?? 'Não informado',
            },
            {
              label: 'Conciliação financeira neste evento',
              value: ledgerLabels.get(String(item.detail.ledgerStatus)) ?? 'Não informada',
            },
          ]}
        />
      ) : null}
      {rows.map((row) => (
        <div className={ledgerDetailRow} key={row.label}>
          <span className={ledgerDetailLabel}>{row.label}</span>
          <span className={ledgerDetailValue}>
            {row.precise ? (
              <StatementDetailPreciseMoney valueToRead={row.value} />
            ) : (
              <StatementDetailMoney valueToRead={row.value} />
            )}
          </span>
        </div>
      ))}
      {availableAt || item.eventType === 'SALE_SETTLED' ? (
        <div className={ledgerDetailRow}>
          <span className={ledgerDetailLabel}>Disponibilidade</span>
          <span className={ledgerDetailValue}>
            {statementAvailabilityLabel(
              item,
              availableAt ? formatDateOnly(availableAt) : undefined,
            )}
          </span>
        </div>
      ) : null}
    </>
  )
}

function statementItemLabel(item: Record<string, unknown>): string {
  const kind = typeof item.kind === 'string' ? item.kind : undefined
  const type = typeof item.type === 'string' ? item.type : undefined
  return ledgerKindLabel(kind) ?? (type ? enumLabel(type) : 'Item')
}

function StatementDetailItems({ item }: { item: StatementJournalItem }) {
  const items = statementDetailRecords(item.detail.items)
  if (items.length === 0) {
    return null
  }

  return (
    <>
      {items.map((entry, index) => (
        <div
          className={ledgerDetailRow}
          key={`statement-item-${statementDetailKey(entry.ledgerEntryId, index)}`}
        >
          <span className={ledgerDetailLabel}>{statementItemLabel(entry)}</span>
          <span className={ledgerDetailValue}>
            <StatementDetailMoney valueToRead={entry.amount} />
          </span>
        </div>
      ))}
      {(['userAmount', 'reserveAmount'] as const).map((key) => {
        const total = statementDetailMoney(item, key)
        const types = key === 'userAmount' ? ['USER', 'COMMISSION'] : ['RESERVE']
        const parts = items
          .filter((entry) => types.includes(String(entry.type)))
          .map((entry) => moneyFromUnknown(entry.amount))
          .filter((value): value is MoneyDto => value !== null)
        return total && parts.length > 0 ? (
          <MoneyReconciliation
            key={key}
            total={total}
            parts={parts}
            label={
              key === 'userAmount' ? 'Arredondamento dos recebíveis' : 'Arredondamento das reservas'
            }
          />
        ) : null
      })}
    </>
  )
}

function AdminLedgerDrawer({
  item,
  usersById,
  usersLoading = false,
  onViewSale,
  onClose,
}: {
  item: AdminStatementItem | null
  usersById: ReturnType<typeof useAdminUsersLookup>['usersById']
  usersLoading?: boolean
  onViewSale: (saleId: string) => void
  onClose: () => void
}) {
  const userDisplay = item?.recipientId
    ? adminUserDisplay({
        userId: item.recipientId,
        usersById,
        isLoading: usersLoading,
      })
    : null
  const saleId = item ? financeEntrySaleId(item) : undefined
  const technicalRows = item
    ? [
        ...(saleId ? [{ label: 'Venda', value: saleId }] : []),
        {
          label: isStatementJournalItem(item) ? 'Lançamento' : 'Recebível',
          value: item.id,
        },
        ...(isStatementJournalItem(item)
          ? [{ label: 'Evento de origem', value: item.eventId }]
          : []),
      ]
    : []

  return (
    <Drawer
      open={item !== null}
      onClose={onClose}
      title={item ? financeEntryTitle(item) : 'Detalhe financeiro'}
      description={
        item ? financeEntrySubtitle(item) : 'Origem, composição, participante e referências.'
      }
      size="wide"
      variant="financial"
      status={
        item ? (
          <StatusBadge label={financeEntryStatusLabel(item)} tone={financeEntryStatusTone(item)} />
        ) : undefined
      }
    >
      {item ? (
        <div className={ledgerDetail}>
          <DetailHero
            eyebrow={
              isStatementJournalItem(item)
                ? {
                    IN: 'Entrada',
                    OUT: 'Saída',
                    INFO: 'Informativo · sem movimentação',
                    HELD: 'Retido em garantia',
                  }[item.direction]
                : 'Posição do recebível'
            }
            value={<StatementMoney item={item} size="xl" />}
            description={
              isStatementJournalItem(item) && item.detail.liabilityCancellation === true
                ? 'Dívida cancelada. Este lançamento não confirma devolução na Stripe.'
                : financeEntryTitle(item)
            }
            aside={formatDateTime(financeEntryDate(item))}
          />
          <KeyValueGrid
            items={[
              { label: 'Pessoa', value: userDisplay?.primary ?? 'VENDEPAY' },
              {
                label: 'Origem',
                value: isStatementJournalItem(item)
                  ? statementEventTypeLabel(item.eventType)
                  : adminLedgerSourceLabel(item.source),
              },
            ]}
          />

          {saleId ? (
            <div className={ledgerReference}>
              <div className={ledgerReferenceLead}>
                <span className={ledgerReferenceTitle}>
                  {isDebtMovement(item) ? 'Venda de origem' : 'Venda vinculada'}
                </span>
                <span className={ledgerReferenceText}>
                  Abra a venda para ver checkout, comprador, taxas e split completo.
                </span>
              </div>
              <NavigationLink onClick={() => onViewSale(saleId)}>Ver venda</NavigationLink>
            </div>
          ) : null}

          <div className={ledgerDetailRows}>
            <div className={ledgerDetailRow}>
              <span className={ledgerDetailLabel}>Pessoa</span>
              <span className={ledgerDetailValue}>
                <span className={buyerCell}>
                  <span className={buyerName}>{userDisplay?.primary ?? 'VENDEPAY'}</span>
                  {userDisplay?.secondary ? (
                    <span className={buyerProduct}>{userDisplay.secondary}</span>
                  ) : null}
                </span>
              </span>
            </div>
            <div className={ledgerDetailRow}>
              <span className={ledgerDetailLabel}>Origem</span>
              <span className={ledgerDetailValue}>
                {isStatementJournalItem(item)
                  ? statementEventTypeLabel(item.eventType)
                  : adminLedgerSourceLabel(item.source)}
              </span>
            </div>
            <div className={ledgerDetailRow}>
              <span className={ledgerDetailLabel}>Data</span>
              <span className={ledgerDetailValue}>{formatDateTime(financeEntryDate(item))}</span>
            </div>
            {!isStatementJournalItem(item) ? (
              <div className={ledgerDetailRow}>
                <span className={ledgerDetailLabel}>Liberação do recebível</span>
                <span className={ledgerDetailValue}>
                  {item.availability?.status === 'available'
                    ? 'Disponibilidade confirmada na conta de pagamento'
                    : item.availableAt
                      ? `Liberação prevista: ${formatDateOnly(item.availableAt)}`
                      : 'Aguardando previsão de liberação'}
                </span>
              </div>
            ) : null}
            {!isStatementJournalItem(item) && item.availability?.cardCountry ? (
              <div className={ledgerDetailRow}>
                <span className={ledgerDetailLabel}>País de emissão do cartão</span>
                <span className={ledgerDetailValue}>{item.availability.cardCountry}</span>
              </div>
            ) : null}
            {isDebtMovement(item) ? (
              <div className={ledgerDetailRow}>
                <span className={ledgerDetailLabel}>Motivo</span>
                <span className={ledgerDetailValue}>{financeEntryTitle(item)}</span>
              </div>
            ) : null}
            {isStatementJournalItem(item) && !isDebtMovement(item) ? (
              <StatementDetailSummaryRows item={item} />
            ) : null}
            {isStatementJournalItem(item) && !isDebtMovement(item) ? (
              <StatementDetailItems item={item} />
            ) : null}
            <div className={ledgerDetailRow}>
              <span className={ledgerDetailLabel}>Moeda</span>
              <span className={ledgerDetailValue}>{item.amount.currency}</span>
            </div>
            {!isStatementJournalItem(item) && item.role ? (
              <div className={ledgerDetailRow}>
                <span className={ledgerDetailLabel}>Papel</span>
                <span className={ledgerDetailValue}>{ledgerRoleLabel(item.role)}</span>
              </div>
            ) : null}
            {!isStatementJournalItem(item) && item.appliedPercentage && item.baseAmount ? (
              <div className={ledgerDetailRow}>
                <span className={ledgerDetailLabel}>Base</span>
                <span className={ledgerDetailValue}>
                  {item.appliedPercentage}% de{' '}
                  <MoneyValue
                    amount={item.baseAmount.amount}
                    currency={item.baseAmount.currency}
                    size="sm"
                  />
                </span>
              </div>
            ) : null}
          </div>

          {isStatementJournalItem(item) ? <FxLossDetails item={item} /> : null}
          <DrawerSection
            title="Rastreabilidade"
            description="Referências para suporte e conciliação."
          >
            <TechnicalDetails rows={technicalRows} />
          </DrawerSection>
        </div>
      ) : null}
    </Drawer>
  )
}

function ChargebackPenaltiesSection({
  query,
  hasPeriod,
  periodLabel,
  currency,
  userFilterActive,
}: {
  query: ReturnType<typeof useAdminChargebackPenalties>
  hasPeriod: boolean
  periodLabel: string
  currency: string
  userFilterActive: boolean
}) {
  const count = query.data?.count ?? 0
  const phrase = count === 1 ? 'multa aplicada' : 'multas aplicadas'
  const scope = userFilterActive ? 'Do usuário filtrado' : 'Na visão global'

  return (
    <section className={panel} aria-label="Chargebacks">
      <div className={titleBlock}>
        <h2 className={panelTitle}>Chargebacks</h2>
        <p className={panelSubtitle}>
          Multas fixas lançadas no período. Cada venda conta uma vez, mesmo quando a taxa é dividida
          entre os participantes.
        </p>
      </div>
      {!hasPeriod ? (
        <Banner tone="info" icon={<ShieldAlert size={18} />} title="Multas de chargeback">
          Escolha um período fechado para ver quantas multas foram aplicadas.
        </Banner>
      ) : query.isPending ? (
        <Skeleton height={72} />
      ) : query.isError ? (
        <Banner tone="warning" icon={<ShieldAlert size={18} />} title="Multas de chargeback">
          Não foi possível carregar a quantidade deste período.
        </Banner>
      ) : (
        <Banner
          tone={count > 0 ? 'danger' : 'info'}
          icon={<ShieldAlert size={18} />}
          title="Multas de chargeback"
          action={
            <span className={penaltyFigure}>
              <span className={penaltyCount}>{count.toLocaleString('pt-BR')}</span>
              <span className={penaltyCaption}>{phrase}</span>
            </span>
          }
        >
          {scope}, {periodLabel}, em {currency}.
        </Banner>
      )}
    </section>
  )
}

export function AdminFinanceScreen({ initialUser }: { initialUser?: AdminLookupUser } = {}) {
  const [view, setView] = useState<FinanceView>(initialUser ? 'statement' : 'receivables')
  const [statementView, setStatementView] = useState<AdminStatementView>('journal')
  const [selection, setSelection] = useState<DashboardSelection>({
    period: initialUser ? 'all' : DEFAULT_DASHBOARD_PERIOD,
    ...(initialUser ? { user: initialUser } : {}),
  })
  const [selectedLedger, setSelectedLedger] = useState<AdminStatementItem | null>(null)
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null)
  const isMobile = useMediaQuery('(max-width: 720px)')
  const periodRange = useMemo(
    () =>
      selection.period === 'custom'
        ? (selection.custom ?? {})
        : adminListPeriodRange(selection.period),
    [selection],
  )
  const periodLabel = dashboardPeriodLabel(selection.period)
  const isToday = selection.period === 'today'
  const resolvedUserId = selection.user?.id
  const currencySummary = useAdminReceivableCurrencies({
    ...(resolvedUserId ? { recipientId: resolvedUserId } : {}),
    ...periodRange,
  })
  const balance = useAdminBalance(resolvedUserId ?? '')
  const availableCurrencies = useMemo(() => {
    const values = new Set<string>()
    for (const item of currencySummary.data?.items ?? []) {
      values.add(item.currency)
    }
    if (resolvedUserId) {
      for (const item of balance.data?.items ?? []) {
        if (balanceHasMoney(item)) {
          values.add(item.currency)
        }
      }
    }
    return sortCurrencies(values)
  }, [balance.data?.items, currencySummary.data?.items, resolvedUserId])
  const currency = 'BRL'
  const penaltyRange =
    periodRange.dateFrom && periodRange.dateTo
      ? { dateFrom: periodRange.dateFrom, dateTo: periodRange.dateTo }
      : null
  const chargebackPenalties = useAdminChargebackPenalties(
    {
      currency,
      dateFrom: penaltyRange?.dateFrom ?? '',
      dateTo: penaltyRange?.dateTo ?? '',
      ...(resolvedUserId ? { recipientId: resolvedUserId } : {}),
    },
    penaltyRange !== null,
  )
  const receivables = useAdminReceivablesInfinite({
    ...(resolvedUserId ? { recipientId: resolvedUserId } : {}),
    currency,
    ...periodRange,
  })
  const receivablesSummary = useAdminReceivablesSummary({
    ...(resolvedUserId ? { recipientId: resolvedUserId } : {}),
    currency,
    ...periodRange,
  })
  const statement = useAdminStatementInfinite(resolvedUserId ?? '', {
    currency,
    view: statementView,
    ...periodRange,
  })
  const balances = useMemo(
    () =>
      (balance.data?.items ?? [])
        .filter((item) => item.currency === currency && balanceHasMoney(item))
        .map(adminBalanceAsSummary),
    [balance.data?.items, currency],
  )
  const statementLedgerItems = useMemo(
    () => statement.data?.pages.flatMap((entry) => entry.items) ?? [],
    [statement.data?.pages],
  )
  const receivableItems = useMemo(
    () => receivables.data?.pages.flatMap((page) => page.items) ?? [],
    [receivables.data?.pages],
  )
  const summary = receivablesSummary.data
  const recipientIds = useMemo(() => {
    const ids = new Set<string>()
    for (const item of statementLedgerItems) {
      if (item.recipientId) ids.add(item.recipientId)
    }
    for (const item of receivableItems) {
      if (item.recipientId) ids.add(item.recipientId)
    }
    if (selectedLedger?.recipientId) ids.add(selectedLedger.recipientId)
    return [...ids]
  }, [receivableItems, selectedLedger, statementLedgerItems])
  const recipientLookup = useAdminUsersLookup(recipientIds)
  const receivableTotals = useMemo(
    () =>
      summary
        ? [{ currency: summary.amount.currency, amount: summary.amount.amount }]
        : sumLedgerByCurrency(receivableItems),
    [receivableItems, summary],
  )
  const pendingReceivables = useMemo(
    () => receivableItems.filter(isPendingLedger),
    [receivableItems],
  )
  const pendingReceivableTotals = useMemo(
    () =>
      summary
        ? [{ currency: summary.pendingAmount.currency, amount: summary.pendingAmount.amount }]
        : sumLedgerByCurrency(receivableItems, isPendingLedger),
    [receivableItems, summary],
  )
  const receivableCount = summary?.count ?? receivableItems.length
  const pendingCount = summary?.pendingCount ?? pendingReceivables.length
  const activeItems = view === 'statement' ? statementLedgerItems : receivableItems
  const userFilterActive = Boolean(resolvedUserId)

  async function handleExport(format: ExportFormat) {
    const ts = exportTimestamp()
    if (view === 'statement' && resolvedUserId) {
      const blob = await exportAdminStatement(
        resolvedUserId,
        { currency, view: statementView, ...periodRange },
        format,
      )
      downloadBlob(blob, `extrato-${ts}.${format}`)
    } else if (view === 'receivables') {
      const blob = await exportAdminReceivables(
        { currency, ...periodRange, ...(resolvedUserId ? { recipientId: resolvedUserId } : {}) },
        format,
      )
      downloadBlob(blob, `recebiveis-${ts}.${format}`)
    }
  }

  const canExportStatement = view === 'statement' && Boolean(resolvedUserId)
  const canExport = view === 'receivables' || canExportStatement

  const chips: ReactNode[] = []
  if (selection.user) {
    chips.push(
      <Chip
        key="user"
        label={`Usuário: ${selection.user.name || selection.user.email}`}
        onRemove={() => {
          setSelection({ ...selection, user: undefined })
        }}
      />,
    )
  }

  return (
    <section className={page} data-testid="finance-screen">
      <div className={header}>
        <div className={titleBlock}>
          <h1 className={title}>Finanças</h1>
          <p className={subtitle}>
            Saldos, recebíveis e extrato com a mesma leitura da carteira normal. Recorte:{' '}
            {periodLabel}.
          </p>
        </div>
        {canExport ? <ExportButton onExport={handleExport} /> : null}
      </div>

      <AdminDashboardFilters
        selection={selection}
        currency={currency}
        currencies={availableCurrencies.length > 0 ? availableCurrencies : [currency]}
        onApply={setSelection}
      />
      <p className={subtitle}>
        Lançamentos: {dashboardRangeLabel(periodRange)}.{' '}
        {selection.user
          ? `Usuário: ${selection.user.name || selection.user.email}`
          : 'Todos os usuários'}
        . Valores em {currency}.
      </p>

      {isToday ? (
        <AdminFinanceHourlyChart
          currency={currency}
          periodLabel={periodLabel}
          {...(resolvedUserId ? { recipientId: resolvedUserId } : {})}
        />
      ) : null}

      <div className={statGrid}>
        <Card padding="md" className={metricCard}>
          <div className={metricHeader}>
            <ReceiptText size={16} />
            <span className={metricLabel}>Recebíveis lançados</span>
          </div>
          <span className={kpiValue}>{receivableCount}</span>
          <MetricMoneyList rows={receivableTotals} emptyCurrency={currency} />
          <span className={metricCount}>
            {userFilterActive ? 'do usuário filtrado' : 'na visão global'} {periodLabel}
          </span>
        </Card>
        <Card padding="md" className={metricCard}>
          <div className={metricHeader}>
            <Clock size={16} />
            <span className={metricLabel}>Pendentes e processando</span>
          </div>
          <MetricMoneyList rows={pendingReceivableTotals} emptyCurrency={currency} />
          <span className={metricCount}>
            {pendingCount} {pendingCount === 1 ? 'lançamento' : 'lançamentos'} em {currency}
          </span>
          <span className={metricCount}>Reservas e taxas também entram neste total.</span>
        </Card>
        <Card padding="md" className={metricCard}>
          <div className={metricHeader}>
            <WalletCards size={16} />
            <span className={metricLabel}>Total lançado</span>
          </div>
          <MetricMoneyList rows={receivableTotals} emptyCurrency={currency} />
          <span className={metricCount}>
            somatório dos recebíveis em {currency} · {periodLabel}
          </span>
        </Card>
      </div>

      <ChargebackPenaltiesSection
        query={chargebackPenalties}
        hasPeriod={penaltyRange !== null}
        periodLabel={periodLabel}
        currency={currency}
        userFilterActive={userFilterActive}
      />

      <Card padding="md" className={tableCard}>
        <div className={viewTabs} role="group" aria-label="Alternar visão financeira">
          {financeViewOptions.map((tab) => (
            <button
              key={tab.id}
              type="button"
              aria-pressed={view === tab.id}
              className={statusOption({ active: view === tab.id })}
              onClick={() => setView(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {view === 'statement' ? (
          <div className={viewTabs} role="group" aria-label="Alternar visão do extrato">
            {statementViewOptions.map((tab) => (
              <button
                key={tab.id}
                type="button"
                aria-pressed={statementView === tab.id}
                className={statusOption({ active: statementView === tab.id })}
                onClick={() => setStatementView(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : null}

        <FilterToolbar
          chips={chips.length > 0 ? chips : null}
          onClear={
            userFilterActive
              ? () => {
                  setSelection({ ...selection, user: undefined })
                }
              : undefined
          }
          resultCount={
            <span className={resultCount}>
              {view === 'statement' ? activeItems.length : receivableCount}{' '}
              {(view === 'statement' ? activeItems.length : receivableCount) === 1
                ? 'resultado'
                : 'resultados'}
            </span>
          }
        >
          {null}
        </FilterToolbar>

        {view === 'statement' && !resolvedUserId ? (
          <EmptyState
            icon={<ReceiptText size={22} />}
            title="Selecione um usuário para ver o extrato"
            description="Abra Mais filtros e selecione um usuário para consultar as movimentações da carteira."
          />
        ) : view === 'statement' ? (
          statement.isPending ? (
            <Skeleton height={320} />
          ) : statement.isError ? (
            <ErrorState
              onRetry={() => {
                void statement.refetch()
              }}
            />
          ) : (
            <AdminStatementList
              items={statementLedgerItems}
              usersById={recipientLookup.usersById}
              usersLoading={recipientLookup.isLoading}
              onSelect={setSelectedLedger}
              filtered
              isMobile={isMobile}
              hasMore={statement.hasNextPage}
              loadingMore={statement.isFetchingNextPage}
              onLoadMore={() => {
                void statement.fetchNextPage()
              }}
            />
          )
        ) : receivables.isLoading ? (
          <Skeleton height={220} />
        ) : receivables.isError ? (
          <ErrorState
            onRetry={() => {
              void receivables.refetch()
            }}
          />
        ) : (
          <AdminStatementList
            items={receivableItems}
            usersById={recipientLookup.usersById}
            usersLoading={recipientLookup.isLoading}
            onSelect={setSelectedLedger}
            filtered={userFilterActive || selection.period !== 'all'}
            isMobile={isMobile}
            hasMore={receivables.hasNextPage}
            loadingMore={receivables.isFetchingNextPage}
            onLoadMore={() => {
              void receivables.fetchNextPage()
            }}
          />
        )}
      </Card>

      {resolvedUserId ? (
        balance.isLoading ? (
          <Skeleton height={190} />
        ) : balance.isError ? (
          <ErrorState
            onRetry={() => {
              void balance.refetch()
            }}
          />
        ) : balances.length === 0 ? (
          <EmptyState
            icon={<WalletCards size={22} />}
            title={`Sem saldo em ${currency} para este usuário`}
            description="Quando houver valores disponíveis, reservas ou recebimentos em andamento nesta moeda, os buckets aparecem aqui."
          />
        ) : (
          <AdminBalanceCards balances={balances} />
        )
      ) : (
        <span className={muted}>
          Filtre um usuário para ver saldo disponível, reservas e extrato.
        </span>
      )}

      <AdminLedgerDrawer
        item={selectedLedger}
        usersById={recipientLookup.usersById}
        usersLoading={recipientLookup.isLoading}
        onViewSale={(saleId) => {
          setSelectedLedger(null)
          setSelectedSaleId(saleId)
        }}
        onClose={() => setSelectedLedger(null)}
      />
      <AdminSaleDetailDrawer saleId={selectedSaleId} onClose={() => setSelectedSaleId(null)} />
    </section>
  )
}

function statementAvailabilityLabel(item: StatementJournalItem, date?: string): string {
  const availability = item.detail.availability as { status?: string } | undefined
  if (availability?.status === 'available')
    return 'Disponibilidade confirmada na conta de pagamento'
  return date ? `Liberação prevista: ${date}` : 'Aguardando previsão de liberação'
}
